import { useCallback, useEffect, useState } from "react";
import { Plus, RefreshCw, Users, Pencil, X } from "lucide-react";
import { NavigationSlotPortal } from "@/components/NavigationSlotContext";
import ModuleWorkspaceTabs from "@/components/ModuleWorkspaceTabs";
import useModuleWorkspaceTabs from "@/hooks/useModuleWorkspaceTabs";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const emptyForm = () => ({
  name: "", phone: "", email: "", address: "",
  customer_type: "individual", tax_id: "", notes: "",
});

export default function CustomersPage() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formsByTab, setFormsByTab] = useState({});
  const [formSectionsByTab, setFormSectionsByTab] = useState({});

  const workspace = useModuleWorkspaceTabs({
    moduleKey: "customers",
    listLabel: "Daftar",
  });
  const {
    tabs, activeTabId, activateTab, openNewTab, openEditTab,
    closeTab, markTabDirty, updateTabLabel,
  } = workspace;

  const activeTab = tabs.find((tab) => tab.id === activeTabId);
  const isList = activeTab?.type === "list";
  const editing = activeTab?.type === "edit"
    ? rows.find((row) => String(row.id) === String(activeTab.recordId)) || null
    : null;
  const form = formsByTab[activeTabId] || emptyForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", {
        params: q.trim() ? { q: q.trim() } : {},
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat pelanggan");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  function setField(key, value) {
    setFormsByTab((current) => ({
      ...current,
      [activeTabId]: {
        ...(current[activeTabId] || emptyForm()),
        [key]: value,
      },
    }));
    if (activeTabId) markTabDirty(activeTabId, true);
  }

  function openCreate() {
    const id = openNewTab({ label: "Data Baru" });
    setFormsByTab((current) => ({ ...current, [id]: emptyForm() }));
  }

  function openEdit(row) {
    const id = openEditTab(String(row.id), {
      label: `Edit: ${row.name || row.customer_code || row.id}`,
    });
    setFormsByTab((current) => {
      if (current[id]) return current;
      return {
        ...current,
        [id]: {
          name: row.name || "",
          phone: row.phone || "",
          email: row.email || "",
          address: row.address || "",
          customer_type: row.customer_type || "individual",
          tax_id: row.tax_id || "",
          notes: row.notes || "",
        },
      };
    });
  }

  function requestClose(tab) {
    if (!tab || tab.type === "list") return;
    if (closeTab(tab)) {
      setFormsByTab((current) => {
        const next = { ...current };
        delete next[tab.id];
        return next;
      });
    }
  }

  async function save(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Nama pelanggan wajib diisi");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        tax_id: form.tax_id.trim(),
        notes: form.notes.trim(),
      };

      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Data pelanggan diperbarui");
      } else {
        await api.post("/customers", payload);
        toast.success("Pelanggan berhasil ditambahkan");
      }

      // Tutup tanpa konfirmasi karena penyimpanan sudah berhasil.
      closeTab(activeTabId, { skipConfirm: true });
      setFormsByTab((current) => {
        const next = { ...current };
        delete next[activeTabId];
        return next;
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan pelanggan");
    } finally {
      setSaving(false);
    }
  }

  async function toggleStatus(row) {
    const active = row.active === false;
    try {
      await api.patch(`/customers/${row.id}/status`, { active });
      toast.success(active ? "Pelanggan diaktifkan" : "Pelanggan dinonaktifkan");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengubah status");
    }
  }

  return (
    <div className="page-shell space-y-4">
      {/* LAYER 2: tab modul */}
      <NavigationSlotPortal layer={2}>
        <div
          role="tablist"
          aria-label="Tab modul pelanggan"
          data-global-nav-layer="2"
          className="flex items-center border-b border-slate-300 bg-slate-100 px-1"
        >
          <div
            role="tab"
            aria-selected="true"
            className="border border-b-0 border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-sky-800"
          >
            <Users className="mr-2 inline h-4 w-4" />
            Pelanggan
          </div>
        </div>
      </NavigationSlotPortal>

      {/* LAYER 3: Daftar, Data Baru, Edit */}
      <ModuleWorkspaceTabs
        tabs={tabs}
        activeTabId={activeTabId}
        onActivate={activateTab}
        onClose={requestClose}
        onRequestClose={requestClose}
        layer={3}
      />

      {isList && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Database Pelanggan</h1>
              <p className="mt-1 text-sm text-slate-500">
                Kelola identitas dan kontak pelanggan perusahaan.
              </p>
            </div>
            <Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="mr-2 h-4 w-4" /> Tambah Pelanggan
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama, kode, atau nomor telepon..."
              className="max-w-md bg-white"
            />
            <span className="text-sm text-slate-500">{rows.length} pelanggan</span>
            <Button variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Muat Ulang
            </Button>
          </div>

          <div className="overflow-x-auto rounded-xl border bg-white">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="p-3">Kode</th>
                  <th className="p-3">Pelanggan</th>
                  <th className="p-3">Kontak</th>
                  <th className="p-3">Jenis</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-500">Memuat data...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="6" className="p-6 text-center text-slate-500">Belum ada pelanggan.</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="p-3 font-mono text-xs">{row.customer_code || "—"}</td>
                    <td className="p-3">
                      <div className="font-semibold">{row.name}</div>
                      <div className="text-xs text-slate-500">{row.email || "—"}</div>
                    </td>
                    <td className="p-3">{row.phone || "—"}</td>
                    <td className="p-3">{row.customer_type === "company" ? "Perusahaan" : "Perorangan"}</td>
                    <td className="p-3">
                      <span className={`rounded-full px-2 py-1 text-xs ${row.active === false ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`}>
                        {row.active === false ? "Nonaktif" : "Aktif"}
                      </span>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="mr-1 text-orange-700" onClick={() => openEdit(row)}>
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => toggleStatus(row)}>
                        {row.active === false ? "Aktifkan" : "Nonaktifkan"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!isList && (
        <div className="rounded-xl border bg-white p-5">
          <div className="mb-5 flex items-center justify-between border-b pb-3">
            <div>
              <h2 className="font-semibold">{editing ? "Edit Pelanggan" : "Data Baru"}</h2>
              <p className="mt-1 text-xs text-slate-500">
                Lengkapi identitas dan informasi kontak pelanggan.
              </p>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => requestClose(activeTab)}>
              <X className="mr-1 h-4 w-4" /> Tutup Tab
            </Button>
          </div>

          <div className="mb-4 flex flex-wrap border-b">
            {[
              ["customer", "Pelanggan"],
              ["address", "Alamat"],
              ["tax", "Pajak"],
            ].map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={(formSectionsByTab[activeTabId] || "customer") === key}
                onClick={() => setFormSectionsByTab((current) => ({
                  ...current, [activeTabId]: key
                }))}
                className={`border border-b-0 px-4 py-2.5 text-sm ${
                  (formSectionsByTab[activeTabId] || "customer") === key
                    ? "border-slate-300 bg-white font-semibold text-sky-800"
                    : "border-transparent bg-slate-100 text-slate-600 hover:bg-white"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={save} className="space-y-5">
            {(formSectionsByTab[activeTabId] || "customer") === "customer" && (
              <div className="grid gap-x-6 gap-y-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <h3 className="border-b pb-2 text-base font-semibold text-sky-700 md:col-span-2">
                    Data Pelanggan
                  </h3>
                  <label className="text-sm font-medium">Jenis pelanggan</label>
                  <Select value={form.customer_type} onValueChange={(v) => setField("customer_type", v)}>
                    <SelectTrigger><SelectValue placeholder="Pilih jenis pelanggan" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Perorangan</SelectItem>
                      <SelectItem value="company">Perusahaan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Nama pelanggan *</label>
                  <Input value={form.name} onChange={(e) => setField("name", e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Nomor telepon</label>
                  <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Email</label>
                  <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <label className="text-sm font-medium">Catatan</label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setField("notes", e.target.value)} />
                </div>
              </div>
            )}

            {(formSectionsByTab[activeTabId] || "customer") === "address" && (
              <section className="max-w-4xl space-y-3">
                <h3 className="border-b pb-2 text-base font-semibold text-sky-700">Alamat Pelanggan</h3>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Alamat utama</label>
                  <Textarea rows={5} value={form.address} onChange={(e) => setField("address", e.target.value)} />
                </div>
              </section>
            )}

            {(formSectionsByTab[activeTabId] || "customer") === "tax" && (
              <section className="max-w-4xl space-y-3">
                <h3 className="border-b pb-2 text-base font-semibold text-sky-700">Informasi Pajak</h3>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">NPWP / Identitas pajak</label>
                  <Input value={form.tax_id} onChange={(e) => setField("tax_id", e.target.value)} />
                </div>
              </section>
            )}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button type="button" variant="outline" onClick={() => requestClose(activeTab)} disabled={saving}>
                Batal
              </Button>
              <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700">
                {saving ? "Menyimpan..." : "Simpan Pelanggan"}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
